import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  Tooltip,
  useTheme,
  alpha
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  MoreVert,
  InfoOutlined
} from '@mui/icons-material';

interface KPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ReactNode;
  trend?: {
    value: number;
    isPositive: boolean;
    period?: string;
  };
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  loading?: boolean;
  onClick?: () => void;
  onInfo?: () => void;
  onMenu?: () => void;
  sx?: any;
}

const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  subtitle,
  icon,
  trend,
  color = 'primary',
  loading = false,
  onClick,
  onInfo,
  onMenu,
  sx
}) => {
  const theme = useTheme();

  const getColorConfig = (color: string) => {
    switch (color) {
      case 'success':
        return {
          main: theme.palette.success.main,
          light: alpha(theme.palette.success.main, 0.1),
          contrast: theme.palette.success.contrastText
        };
      case 'warning':
        return {
          main: theme.palette.warning.main,
          light: alpha(theme.palette.warning.main, 0.1),
          contrast: theme.palette.warning.contrastText
        };
      case 'error':
        return {
          main: theme.palette.error.main,
          light: alpha(theme.palette.error.main, 0.1),
          contrast: theme.palette.error.contrastText
        };
      case 'info':
        return {
          main: theme.palette.info.main,
          light: alpha(theme.palette.info.main, 0.1),
          contrast: theme.palette.info.contrastText
        };
      case 'secondary':
        return {
          main: theme.palette.secondary.main,
          light: alpha(theme.palette.secondary.main, 0.1),
          contrast: theme.palette.secondary.contrastText
        };
      default:
        return {
          main: theme.palette.primary.main,
          light: alpha(theme.palette.primary.main, 0.1),
          contrast: theme.palette.primary.contrastText
        };
    }
  };

  const colorConfig = getColorConfig(color);

  const getTrendIcon = () => {
    if (!trend) return null;
    
    if (trend.value > 0) {
      return <TrendingUp sx={{ fontSize: 16, color: theme.palette.success.main }} />;
    } else if (trend.value < 0) {
      return <TrendingDown sx={{ fontSize: 16, color: theme.palette.error.main }} />;
    } else {
      return <TrendingFlat sx={{ fontSize: 16, color: theme.palette.grey[500] }} />;
    }
  };

  const getTrendColor = () => {
    if (!trend) return theme.palette.text.secondary;
    
    if (trend.value > 0) {
      return theme.palette.success.main;
    } else if (trend.value < 0) {
      return theme.palette.error.main;
    } else {
      return theme.palette.grey[500];
    }
  };

  const formatValue = (val: string | number) => {
    if (typeof val === 'number') {
      if (val >= 1000000) {
        return `${(val / 1000000).toFixed(1)}M`;
      } else if (val >= 1000) {
        return `${(val / 1000).toFixed(1)}K`;
      }
      return val.toLocaleString();
    }
    return val;
  };

  return (
    <Card
      sx={{
        position: 'relative',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        minHeight: '130px',
        display: 'flex',
        flexDirection: 'column',
        ...(theme.palette.mode === 'dark' && {
          backgroundColor: 'rgba(255,255,255,0.03)',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.4)'
        }),
        '&:hover': onClick ? {
          transform: 'scale(1.02) translateY(-2px)',
          ...(theme.palette.mode === 'dark' && {
            backgroundColor: 'rgba(255,255,255,0.05)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.6)'
          }),
          ...(theme.palette.mode === 'light' && {
            backgroundColor: 'rgba(33,150,243,0.05)',
            boxShadow: theme.shadows[8]
          })
        } : {},
        ...sx
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
        {/* Header with icon and actions */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            {/* Icon */}
            {icon && (
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  backgroundColor: colorConfig.light,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2,
                  color: colorConfig.main
                }}
              >
                {icon}
              </Box>
            )}
            
            {/* Title */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  lineHeight: 1.2
                }}
              >
                {title}
              </Typography>
            </Box>
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {onInfo && (
              <Tooltip title="Informatii">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onInfo();
                  }}
                  sx={{ color: 'text.secondary' }}
                >
                  <InfoOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            
            {onMenu && (
              <Tooltip title="Optiuni">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMenu();
                  }}
                  sx={{ color: 'text.secondary' }}
                >
                  <MoreVert fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Value */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            mb: subtitle ? 1 : 0,
            fontSize: '2rem',
            lineHeight: 1.2
          }}
        >
          {loading ? '...' : formatValue(value)}
        </Typography>

        {/* Subtitle */}
        {subtitle && (
          <Typography
            variant="body2"
            sx={{
              mb: trend ? 1 : 0,
              fontSize: '0.875rem',
              color: theme.palette.mode === 'dark' ? '#b0b0b0' : 'text.secondary'
            }}
          >
            {subtitle}
          </Typography>
        )}

        {/* Trend */}
        {trend && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {getTrendIcon()}
            <Typography
              variant="body2"
              sx={{
                color: getTrendColor(),
                fontWeight: 500,
                fontSize: '0.875rem'
              }}
            >
              {Math.abs(trend.value)}%
            </Typography>
            {trend.period && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.75rem' }}
              >
                {trend.period}
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default KPICard;
